import { type ReactNode, useEffect, useState } from 'react';
import {
  MsalProvider,
  AuthenticatedTemplate,
  UnauthenticatedTemplate,
  useMsal,
} from '@azure/msal-react';
import {
  PublicClientApplication,
  EventType,
  type AuthenticationResult,
  InteractionStatus,
  InteractionRequiredAuthError,
  BrowserAuthError,
} from '@azure/msal-browser';
import { msalConfig, loginRequest, isInIframe } from './msalConfig';
import LoginPage from './LoginPage';

export const msalInstance = new PublicClientApplication(msalConfig);

// Set the first account as active if available
const accounts = msalInstance.getAllAccounts();
if (accounts.length > 0) {
  msalInstance.setActiveAccount(accounts[0]);
}

msalInstance.addEventCallback((event) => {
  if (event.eventType === EventType.LOGIN_SUCCESS && event.payload) {
    const payload = event.payload as AuthenticationResult;
    msalInstance.setActiveAccount(payload.account);
  }
});

function AuthGate({ children }: { children: ReactNode }) {
  const { instance, inProgress } = useMsal();
  const [authChecked, setAuthChecked] = useState(false);
  const [ssoAttempted, setSsoAttempted] = useState(false);

  // Handle redirect promise on mount
  useEffect(() => {
    instance.handleRedirectPromise().then((response) => {
      if (response?.account) {
        instance.setActiveAccount(response.account);
      }
    }).catch(() => {
      // Redirect handling failed, will proceed to SSO attempt
    });
  }, [instance]);

  // Attempt silent SSO when no accounts exist and MSAL is idle
  useEffect(() => {
    if (ssoAttempted) return;
    if (inProgress !== InteractionStatus.None) return;

    const existingAccounts = instance.getAllAccounts();
    if (existingAccounts.length > 0) {
      // Already have an account, no need for SSO
      setAuthChecked(true);
      setSsoAttempted(true);
      return;
    }

    // No accounts — try silent SSO using the existing Microsoft session cookie
    setSsoAttempted(true);

    instance.ssoSilent({
      scopes: loginRequest.scopes,
    }).then((response) => {
      if (response?.account) {
        instance.setActiveAccount(response.account);
      }
      setAuthChecked(true);
    }).catch((error) => {
      // Silent SSO failed — this is expected when there's no Microsoft session
      if (error instanceof InteractionRequiredAuthError ||
          error instanceof BrowserAuthError) {
        console.debug('[Nexus Auth] ssoSilent failed, showing login page:', error.errorCode);
      } else {
        console.warn('[Nexus Auth] ssoSilent unexpected error:', error);
      }
      setAuthChecked(true);
    });
  }, [instance, inProgress, ssoAttempted]);

  // Show loading spinner while checking auth
  if (!authChecked || inProgress !== InteractionStatus.None) {
    return (
      <div className="min-h-screen bg-[#0A1628] flex items-center justify-center">
        <div className="text-center">
          <div className="w-10 h-10 border-4 border-gold-primary border-t-transparent rounded-full animate-spin mx-auto mb-4" />
          <p className="text-text-muted text-sm">
            {isInIframe() ? 'Signing in...' : 'Authenticating...'}
          </p>
        </div>
      </div>
    );
  }

  return (
    <>
      <AuthenticatedTemplate>{children}</AuthenticatedTemplate>
      <UnauthenticatedTemplate>
        <LoginPage />
      </UnauthenticatedTemplate>
    </>
  );
}

export function AuthProvider({ children }: { children: ReactNode }) {
  return (
    <MsalProvider instance={msalInstance}>
      <AuthGate>{children}</AuthGate>
    </MsalProvider>
  );
}

/** Helper to get the current user info from MSAL */
export function useAuthUser() {
  const { accounts } = useMsal();
  const account = accounts[0];

  if (!account) {
    return null;
  }

  return {
    name: account.name || 'User',
    email: account.username || '',
    initials: (account.name || 'U')
      .split(' ')
      .map((n) => n[0])
      .join('')
      .toUpperCase()
      .slice(0, 2),
  };
}
